/**
 * Auth seam. In mock mode it fakes a session; with VITE_USE_MOCKS=false it hits
 * the real NestJS API. On login the access token is kept in memory and the
 * refresh token rides in a Secure httpOnly cookie (set by the API) — so a hard
 * reload keeps you signed in: bootstrapSession() silently trades the cookie for
 * a fresh access token on boot. The mock→real flip is the single env change.
 */
import { USE_MOCKS, mockDelay, apiGet, apiPost, setRefreshHandler } from './client';
import { useSession, type ActiveRole } from '../stores/session';

export interface AuthUser {
  id: string;
  name: string;
  email: string | null;
  role: string;
}

export interface LoginResult {
  accessToken: string;
  user: AuthUser;
}

export interface MeResponse {
  id: string;
  name: string;
  email: string | null;
  username: string | null;
  role: string | null;
  resellerId: number | null;
  businessId: number | null;
  platformRole: string | null;
  mfaEnabled: boolean;
  themePreference: string;
  preferredCurrency: string;
}

/** Map the JWT actor role to one of the four UI shells. */
function actorRoleToShell(role: string | null): ActiveRole {
  if (!role) return 'owner';
  if (role.startsWith('platform')) return 'admin';
  if (role.startsWith('reseller')) return 'reseller';
  if (role === 'business_staff') return 'staff';
  return 'owner';
}

/** Route to the right shell's root for a signed-in role (each shell is a route tree). */
export function destinationForRole(role: string | null): string {
  if (!role) return '/';
  if (role.startsWith('platform')) return '/admin';
  if (role.startsWith('reseller')) return '/reseller';
  if (role === 'business_staff') return '/staff';
  return '/';
}

/** Sign in. Stores the access token + user in the session store (real mode); the refresh token is the httpOnly cookie the API set. */
export async function login(identifier: string, password: string): Promise<LoginResult> {
  if (USE_MOCKS) {
    const result: LoginResult = {
      accessToken: 'mock-access',
      user: { id: 'usr_10', name: 'Imran Khan', email: identifier, role: 'business_admin' },
    };
    useSession.getState().setUser({ id: result.user.id, name: result.user.name, email: result.user.email });
    return mockDelay(result);
  }
  const result = await apiPost<LoginResult>('/auth/login', { identifier, password });
  useSession.getState().setTokens(result.accessToken, null); // refresh token lives in the httpOnly cookie
  useSession.getState().setUser({ id: result.user.id, name: result.user.name, email: result.user.email });
  useSession.getState().setActiveRole(actorRoleToShell(result.user.role));
  return result;
}

/** Current identity (real mode). */
export const fetchMe = () => apiGet<MeResponse>('/auth/me');

// ── Direct signup (P0 #1) ────────────────────────────────────────────────────
export interface SignupPayload {
  email: string;
  mobile?: string;
  password: string;
  fullName: string;
  /** Chosen POS/portal login username. Optional — the POS derives one when absent. */
  username?: string;
  businessName: string;
  subdomain?: string;
  packageId: string;
  interval: string;
  currencyCode: string;
  dbMode: string;
  startTrial: boolean;
}
export interface SignupResult {
  accessToken: string;
  user: AuthUser;
  business: { id: string; name: string; slug: string };
  startTrial: boolean;
  priceId: string;
}
/** Start a password reset — emails a recovery code (always resolves; no enumeration). */
export const forgotPassword = (email: string): Promise<{ ok: boolean; devCode?: string }> =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPost('/auth/forgot-password', { email });
/** Complete a password reset with the emailed code. */
export const resetPasswordWithCode = (email: string, code: string, newPassword: string): Promise<{ ok: boolean }> =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPost('/auth/reset-password', { email, code, newPassword });

/** Send the signup verification code to the email (+ phone). */
export const sendSignupOtp = (email: string, mobile?: string): Promise<{ devCode?: string }> =>
  USE_MOCKS ? mockDelay({}) : apiPost('/auth/signup/send-otp', { email, mobile });

/** Which public signup doors are open (admin can close them). Pages hide a closed form. */
export type SignupAvailability = { directSignup: boolean; resellerApplications: boolean };
export const getSignupAvailability = (): Promise<SignupAvailability> =>
  USE_MOCKS ? mockDelay({ directSignup: true, resellerApplications: true }) : apiGet<SignupAvailability>('/signup-availability');
/** Verify the signup OTP for an email. */
export const verifySignupOtp = (email: string, code: string): Promise<boolean> =>
  USE_MOCKS ? mockDelay(code === '1234') : apiPost<{ ok: boolean }>('/auth/verify-otp', { email, code }).then((r) => r.ok);
/** Create the account + business (gated behind a verified OTP); the API auto-logs-in and sets the refresh cookie. */
export const signupAccount = (payload: SignupPayload): Promise<SignupResult> =>
  USE_MOCKS
    ? mockDelay({
        accessToken: 'mock-signup',
        user: { id: 'usr_new', name: payload.fullName, email: payload.email, role: 'business_admin' },
        business: { id: 'biz_new', name: payload.businessName, slug: payload.subdomain ?? 'new-business' },
        startTrial: payload.startTrial, priceId: 'price_mock',
      })
    : apiPost<SignupResult>('/auth/signup', payload);

// ── Invitations (accept flow — public, token-gated) ──────────────────────────
export interface InviteContext {
  email: string;
  name: string | null;
  scope: 'platform' | 'reseller' | 'business';
  roleLabel: string;
  posOnly: boolean;
  orgName: string | null;
  inviterName: string | null;
  status: string;
  expired: boolean;
  valid: boolean;
}
const MOCK_INVITE: InviteContext = {
  email: 'rina@khan-bakery.com', name: 'Rina Akter', scope: 'business', roleLabel: 'Cashier',
  posOnly: true, orgName: 'Khan Bakery', inviterName: 'Imran Khan', status: 'pending', expired: false, valid: true,
};
/** Invite context for the accept page (the URL token is the secret). */
export const getInvite = (token: string) =>
  USE_MOCKS ? mockDelay(MOCK_INVITE) : apiGet<InviteContext>(`/auth/invite/${encodeURIComponent(token)}`);
/** Accept an invite — set password, create/attach the account + role. */
export const acceptInvite = (token: string, body: { name?: string; password: string }) =>
  USE_MOCKS
    ? mockDelay({ email: MOCK_INVITE.email, passwordSet: true })
    : apiPost<{ email: string; passwordSet: boolean }>(`/auth/invite/${encodeURIComponent(token)}/accept`, body);

/**
 * Rotate the refresh cookie → a fresh access token. No token is read from JS —
 * the cookie travels automatically (credentials: 'include'). De-duplicated: a
 * single in-flight rotation is shared, so React StrictMode's double-mount and a
 * burst of concurrent 401s can't replay a rotated token (which would trip the
 * server's reuse-detection and revoke the whole family).
 */
let refreshInflight: Promise<boolean> | null = null;
export function refreshSession(): Promise<boolean> {
  if (USE_MOCKS) return Promise.resolve(true);
  // During read-only impersonation, a 401 means the short-lived impersonation
  // token expired. The httpOnly cookie is still the ADMIN's, so rotating it would
  // graft an admin token into the reseller shell. Instead, drop impersonation and
  // hard-navigate back to where the admin opened it (reload → admin cookie →
  // admin session restored).
  const sess = useSession.getState();
  if (sess.impersonation) {
    const returnTo = sess.impersonation.returnTo;
    sess.exitImpersonation();
    if (typeof window !== 'undefined') window.location.href = returnTo;
    return Promise.resolve(false);
  }
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const res = await apiPost<{ accessToken: string }>('/auth/refresh');
      useSession.getState().setTokens(res.accessToken, null);
      return true;
    } catch {
      useSession.getState().signOut();
      return false;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

/**
 * App-boot session restore: silently refresh from the httpOnly cookie, then
 * hydrate the user from /auth/me. Returns whether a session was restored.
 */
export async function bootstrapSession(): Promise<boolean> {
  if (USE_MOCKS) return true;
  const ok = await refreshSession();
  if (!ok) return false;
  try {
    const me = await fetchMe();
    useSession.getState().setUser({ id: me.id, name: me.name, email: me.email });
    useSession.getState().setActiveRole(actorRoleToShell(me.role));
  } catch {
    /* token restored but the profile fetch failed — leave the session as-is */
  }
  return true;
}

/** Sign out — revoke server-side + clear the cookie (real mode) and the local session either way. */
export async function logout(): Promise<void> {
  if (!USE_MOCKS) {
    try {
      await apiPost('/auth/logout');
    } catch {
      /* clearing locally regardless */
    }
  }
  useSession.getState().signOut();
}

// Let the api client transparently refresh-and-retry on a 401.
setRefreshHandler(refreshSession);
